import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Field, Notice } from '../patterns';
import type { Tenant } from '../../services/tenantService';

interface RoleOpt { _id: string; name: string; }

interface UserShape {
  _id: string;
  username: string;
  name: string;
  email?: string;
  tenantId: string;
  role: any;
  active: boolean;
}

interface Props {
  show: boolean;
  user: UserShape | null;
  tenants: Tenant[];
  fetchRolesFor: (tenantId: string) => Promise<RoleOpt[]>;
  onHide: () => void;
  onSave: (input: {
    username?: string;
    email?: string;
    name: string;
    tenantId: string;
    role: string;
    active: boolean;
  }) => Promise<void>;
}

const UserEditModal: React.FC<Props> = ({ show, user, tenants, fetchRolesFor, onHide, onSave }) => {
  const { t } = useTranslation();
  const isEdit = user !== null;

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [role, setRole] = useState('');
  const [active, setActive] = useState(true);
  const [roles, setRoles] = useState<RoleOpt[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setUsername(user.username);
      setEmail(user.email || '');
      setName(user.name);
      setTenantId(user.tenantId);
      setRole(user.role?._id || user.role || '');
      setActive(user.active);
    } else {
      setUsername(''); setEmail(''); setName('');
      setTenantId(''); setRole(''); setActive(true);
    }
    setError(null);
  }, [user, show]);

  useEffect(() => {
    if (!tenantId) { setRoles([]); return; }
    let alive = true;
    fetchRolesFor(tenantId).then(rs => { if (alive) setRoles(rs); });
    return () => { alive = false; };
  }, [tenantId, fetchRolesFor]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload: any = {
        name: name.trim(),
        email: email.trim() || undefined,
        tenantId,
        role,
        active,
      };
      if (!isEdit) payload.username = username.trim();
      await onSave(payload);
      onHide();
    } catch (err: any) {
      setError(err?.response?.data?.error || t('system.users.errors.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} title={isEdit ? t('system.users.modal.editTitle') : t('system.users.modal.createTitle')} size="md">
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="pat-fgrid-2">
            <Field label={t('system.users.modal.fields.username')} controlId="username" required>
              <input id="username" className="pat-input" value={username} onChange={e => setUsername(e.target.value)} disabled={isEdit} required />
            </Field>
            <Field label={t('system.users.modal.fields.name')} controlId="name">
              <input id="name" className="pat-input" value={name} onChange={e => setName(e.target.value)} required />
            </Field>
            <Field label={t('system.users.modal.fields.email')} controlId="email">
              <input id="email" type="email" className="pat-input" value={email} onChange={e => setEmail(e.target.value)} />
            </Field>
            <Field label={t('system.users.modal.fields.tenantId')} controlId="tenantId">
              <select id="tenantId" className="pat-input" value={tenantId} onChange={e => setTenantId(e.target.value)} disabled={isEdit} required>
                <option value="">—</option>
                {tenants.map(tn => <option key={tn._id} value={tn.tenantId}>{tn.name} ({tn.tenantId})</option>)}
              </select>
            </Field>
            <Field label={t('system.users.modal.fields.role')} controlId="role">
              <select id="role" className="pat-input" value={role} onChange={e => setRole(e.target.value)} required disabled={!tenantId}>
                <option value="">—</option>
                {roles.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
              </select>
            </Field>
            <Field label={t('system.users.modal.fields.active')} controlId="active">
              <input id="active" type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} />
            </Field>
          </div>
          {error && <Notice tone="danger">{error}</Notice>}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="pat-btn pat-btn--subtle" onClick={onHide} disabled={submitting}>
            {t('system.users.modal.cancel')}
          </button>
          <button type="submit" className="pat-btn pat-btn--primary" disabled={submitting}>
            {t('system.users.modal.save')}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default UserEditModal;
